import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useToaster } from '../../../core/components/Toast.tsx';
import { SettingsCard } from '../../../core/components/SettingsFormCard.tsx';
import Button from '../../../core/components/Button.tsx';
import Loader from '../../../core/components/Loader.tsx';
import { GET_CUSTOMIZATION_PARAMS, UPDATE_CUSTOMIZATION_PARAMS } from '../../queries.ts';
import SchemaForm, { FieldError, JsonSchema } from './SchemaForm.tsx';

interface CustomizationParamsConfigProps {
    channelId: string;
}

const parseJsonString = (value: string | null | undefined, fallback: any) => {
    if (!value) return fallback;
    try {
        return JSON.parse(value);
    } catch {
        return fallback;
    }
};

const CustomizationParamsConfig = ({ channelId }: CustomizationParamsConfigProps) => {
    const { t } = useTranslation('config');
    const { publishToast } = useToaster();

    const { data, loading, refetch } = useQuery(GET_CUSTOMIZATION_PARAMS, {
        variables: { channelId },
        fetchPolicy: 'cache-and-network',
    });

    const schema: JsonSchema | null = useMemo(
        () => parseJsonString(data?.customizationParams?.schema, null),
        [data?.customizationParams?.schema]
    );
    const initialParams = useMemo(
        () => parseJsonString(data?.customizationParams?.params, {}),
        [data?.customizationParams?.params]
    );

    const [formValue, setFormValue] = useState<Record<string, any>>({});
    const [fieldErrors, setFieldErrors] = useState<FieldError[]>([]);

    useEffect(() => {
        setFormValue(initialParams || {});
        setFieldErrors([]);
    }, [initialParams]);

    const [updateParams, { loading: saving }] = useMutation(UPDATE_CUSTOMIZATION_PARAMS);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const { data: result } = await updateParams({
                variables: { channelId, params: JSON.stringify(formValue) },
            });
            const payload = result?.updateCustomizationParams;
            if (payload?.success) {
                setFieldErrors([]);
                publishToast({
                    description: t('configPage.customizationParams.saveSuccess'),
                    status: 'success',
                });
                await refetch();
            } else {
                setFieldErrors(payload?.errors || []);
                publishToast({
                    description: t('configPage.customizationParams.validationError'),
                    status: 'error',
                });
            }
        } catch (err) {
            console.error(err);
            publishToast({
                description: t('configPage.customizationParams.saveError'),
                status: 'error',
            });
        }
    };

    if (loading && !data) {
        return (
            <SettingsCard title={t('configPage.customizationParams.title')}>
                <Loader />
            </SettingsCard>
        );
    }

    if (!schema) {
        return (
            <SettingsCard
                title={t('configPage.customizationParams.title')}
                description={t('configPage.customizationParams.emptyDescription')}
            >
                <div />
            </SettingsCard>
        );
    }

    return (
        <SettingsCard
            title={t('configPage.customizationParams.title')}
            description={t('configPage.customizationParams.description')}
        >
            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                <SchemaForm
                    schema={schema}
                    value={formValue}
                    onChange={setFormValue}
                    errors={fieldErrors}
                    disabled={saving}
                />
                <div className="flex justify-end">
                    <Button type="submit" variant="primary" disabled={saving}>
                        {t('configPage.customizationParams.saveButton')}
                    </Button>
                </div>
            </form>
        </SettingsCard>
    );
};

export default CustomizationParamsConfig;
